import {
  DashboardWidgetError,
  DashboardWidgetLoading,
} from '@/components/custom/DashboardWidgetState';
import { StatCard } from '@/components/custom/StatCard';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useIndividualsApi } from '@/hooks/useIndividualsApi';
import { usePermissions } from '@/hooks/usePermissions';
import { toApiError } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CalendarDays,
  CheckCircle,
  CreditCard,
  Dumbbell,
  Medal,
  Settings,
  Trophy,
} from 'lucide-react';
import { Link, Route, Routes, useSearchParams } from 'react-router-dom';
import IndividualBillingPage from './IndividualBilling';
import IndividualLeaderboardPage from './IndividualLeaderboard';
import IndividualProfilePage from './IndividualProfile';
import IndividualProgressPage from './IndividualProgress';
import IndividualWorkoutsPage from './IndividualWorkouts';

function IndividualDashboardHome() {
  const individualsApi = useIndividualsApi();
  const { hasPermission } = usePermissions();
  const canLogScores = hasPermission('log_scores');
  const { user: authUser } = useAuthStore();
  const [searchParams] = useSearchParams();
  const signupSuccess = searchParams.get('signup') === 'success';

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['individual-dashboard', authUser?.id],
    queryFn: async () => {
      const response = await individualsApi.getDashboard();
      if (!response.success) throw toApiError(response, 'Failed to load dashboard');
      return response.data;
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });

  // BUG-APR30-006 — Today's Workout hero card. Reuses the existing
  // /individuals/workouts?date=todayStr endpoint so no new API surface.
  const todayStr = new Date().toISOString().split('T')[0];
  const {
    data: todayWorkoutsData,
    isLoading: isTodayWorkoutLoading,
    isFetching: isTodayWorkoutFetching,
    error: todayWorkoutError,
    refetch: refetchTodayWorkout,
  } = useQuery({
    queryKey: ['individual-today-workout', todayStr],
    queryFn: async () => {
      const response = await individualsApi.getWorkouts({ date: todayStr });
      if (!response.success) throw toApiError(response, "Failed to load today's workout");
      return response.data;
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });
  const todayWorkout = todayWorkoutsData?.workouts?.[0] || null;

  const user = data?.user;
  const recentScores = data?.recent_scores || [];
  const totalScores = data?.total_scores || 0;
  const globalRank = data?.global_rank || 0;
  const totalParticipants = data?.total_participants || 0;
  const isSignupSetupPending = isLoading || !data;

  return (
    <DashboardLayout>
      {/* Signup Success Alert */}
      {signupSuccess && (
        <Alert className="mb-6 border-green-500/20 bg-green-500/10 animate-fade-in">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertTitle className="text-green-500">
            {isSignupSetupPending ? 'Payment Successful!' : 'Welcome to ErgShoot!'}
          </AlertTitle>
          <AlertDescription>
            {isSignupSetupPending
              ? 'Your payment was processed and your account is still being activated. Your available shortcuts remain usable while we reconnect.'
              : 'Your account is all set up. Start logging your workouts and track your progress.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">
          Welcome, @{user?.username || 'athlete'}
        </h1>
        <p className="mt-1 text-muted-foreground">Individual Subscription | Personal Dashboard</p>
      </div>

      {/* BUG-APR30-006 — Today's Workout hero card */}
      <Card className="mb-8 border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card animate-fade-in">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base font-medium">
                <CalendarDays className="h-5 w-5 text-primary" />
                Today's Workout
              </CardTitle>
              <CardDescription className="mt-1">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
              </CardDescription>
            </div>
            {todayWorkout?.my_score && (
              <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                <CheckCircle className="h-3 w-3 mr-1" />
                Logged
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isTodayWorkoutLoading ? (
            <DashboardWidgetLoading embedded label="Loading today's workout…" />
          ) : todayWorkoutError ? (
            <DashboardWidgetError
              embedded
              title="Today's workout unavailable"
              description="We couldn't load today's session. Your dashboard and other athlete tools remain available."
              error={todayWorkoutError}
              context={{
                portal: 'individual',
                widget: 'today-workout',
                endpoint: '/individuals/workouts',
              }}
              onRetry={() => void refetchTodayWorkout()}
              isRetrying={isTodayWorkoutFetching}
            />
          ) : todayWorkout ? (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-foreground mb-1">{todayWorkout.name}</h3>
                {todayWorkout.track?.name && (
                  <Badge variant="outline" className="mb-3">
                    <Dumbbell className="h-3 w-3 mr-1" />
                    {todayWorkout.track.name}
                  </Badge>
                )}
                <WorkoutCreatorMetadata
                  attribution={todayWorkout.attribution}
                  created_at={todayWorkout.created_at}
                  created_by_username={todayWorkout.created_by_username}
                  className="mb-2"
                />
                {todayWorkout.description && (
                  <pre className="whitespace-pre-wrap font-mono text-sm text-muted-foreground bg-muted/50 border border-border rounded-md p-3 mt-2">
                    {todayWorkout.description}
                  </pre>
                )}
              </div>
              {canLogScores && (
                <Link to="/individual/workouts">
                  <Button className="w-full sm:w-auto">
                    {todayWorkout.my_score ? 'Update Score' : 'Log Score'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <p className="text-muted-foreground mb-1">Rest day</p>
              <p className="text-sm text-muted-foreground">
                No workout scheduled for today — recover well.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      {isLoading ? (
        <DashboardWidgetLoading
          label={signupSuccess ? 'Setting up your account…' : 'Loading performance overview…'}
        />
      ) : error ? (
        <DashboardWidgetError
          title="Performance overview unavailable"
          description="We couldn't load your latest scores and ranking. Your workouts and account tools remain available."
          error={error}
          context={{ portal: 'individual', widget: 'overview', endpoint: '/individuals/dashboard' }}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Global Rank"
            value={totalScores > 0 ? `#${globalRank}` : '—'}
            subtitle={
              totalParticipants > 0 ? `of ${totalParticipants} athletes` : 'Log scores to rank'
            }
            icon={Trophy}
            delay={0}
          />
          <StatCard
            title="Total Scores"
            value={totalScores}
            subtitle="Workouts logged"
            icon={Medal}
            delay={50}
          />
          <StatCard
            title="Subscription"
            value={user?.subscription_status || 'Individual'}
            subtitle="Active membership"
            icon={CreditCard}
            delay={100}
          />
          <StatCard
            title="This Week"
            value={
              recentScores.filter((s) => {
                const scoreDate = new Date(s.created_at);
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return scoreDate >= weekAgo;
              }).length
            }
            subtitle="Workouts completed"
            icon={Dumbbell}
            delay={150}
          />
        </div>
      )}

      {/* Recent Scores */}
      {!isLoading && !error && (
        <Card className="mt-8 animate-fade-in" style={{ animationDelay: '150ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Medal className="h-5 w-5 text-primary" />
              Recent Scores
            </CardTitle>
            <CardDescription>Your latest workout performances</CardDescription>
          </CardHeader>
          <CardContent>
            {recentScores.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No scores logged yet</p>
                <Link to="/individual/workouts">
                  <Button>
                    Log Your First Score
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentScores.slice(0, 5).map((score, index) => (
                  <div
                    key={score.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 animate-fade-in"
                    style={{ animationDelay: `${200 + index * 30}ms` }}
                  >
                    <div>
                      <p className="font-medium">{score.workout_title}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(score.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-primary">{score.result}</p>
                      {score.rx && <span className="text-xs text-green-500 font-medium">Rx</span>}
                    </div>
                  </div>
                ))}
                {recentScores.length > 5 && (
                  <Link to="/individual/workouts" className="block text-center pt-2">
                    <Button variant="link">View all scores</Button>
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="animate-fade-in" style={{ animationDelay: '200ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Dumbbell className="h-5 w-5 text-primary" />
              Workouts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              View today's workout and log your score.
            </p>
            <Link to="/individual/workouts">
              <Button variant="outline" className="w-full">
                View Workouts
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '250ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Medal className="h-5 w-5 text-primary" />
              Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              See how you rank globally against others.
            </p>
            <Link to="/individual/leaderboard">
              <Button variant="outline" className="w-full">
                View Leaderboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '300ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <CreditCard className="h-5 w-5 text-primary" />
              Billing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Manage your subscription and billing.
            </p>
            <Link to="/individual/billing">
              <Button variant="outline" className="w-full">
                Manage Billing
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="animate-fade-in" style={{ animationDelay: '350ms' }}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Settings className="h-5 w-5 text-primary" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">View and update your profile.</p>
            <Link to="/individual/profile">
              <Button variant="outline" className="w-full">
                View Profile
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export default function IndividualDashboard() {
  return (
    <Routes>
      <Route path="/" element={<IndividualDashboardHome />} />
      <Route path="workouts" element={<IndividualWorkoutsPage />} />
      <Route path="leaderboard" element={<IndividualLeaderboardPage />} />
      <Route path="progress" element={<IndividualProgressPage />} />
      <Route
        path="billing"
        element={
          <DashboardLayout>
            <IndividualBillingPage />
          </DashboardLayout>
        }
      />
      <Route path="profile" element={<IndividualProfilePage />} />
    </Routes>
  );
}
